"use client";

import { FormEvent, useState } from "react";
import axios from "axios";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { SiteHeader } from "@/components/SiteHeader";
import {
  getPhoneVerification,
  login,
  requestPhoneVerification,
  signup,
  type IdentityVerification,
} from "@/features/auth/api";
import { useToastStore } from "@/store/useAppStore";
import {
  signupAgreements,
  SignupAgreementKey,
  SIGNUP_TERMS_VERSION,
} from "@/lib/signup-terms";

export function AuthScreen({ initialMode = "login" }: { initialMode?: "login" | "signup" }) {
  const mode = initialMode;
  const [form, setForm] = useState({
    userId: "",
    password: "",
    name: "",
    email: "",
    phone: "",
    birthDate: "",
    identityVerificationId: "",
  });
  const [verified, setVerified] = useState(false);
  const [pending, setPending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [agreements, setAgreements] = useState<
    Record<SignupAgreementKey, boolean>
  >({
    service: false,
    privacy: false,
    identity: false,
    marketing: false,
  });
  const router = useRouter();
  const showToast = useToastStore((state) => state.showToast);
  const requiredAgreed = signupAgreements
    .filter((agreement) => agreement.required)
    .every((agreement) => agreements[agreement.key]);
  const allAgreed = signupAgreements.every(
    (agreement) => agreements[agreement.key],
  );

  const update = (key: keyof typeof form, value: string) => {
    setForm((current) => ({
      ...current,
      [key]: value,
      ...(["name", "birthDate", "phone"].includes(key)
        ? { identityVerificationId: "" }
        : {}),
    }));
    if (["name", "birthDate", "phone"].includes(key)) setVerified(false);
  };

  const openKcpAndWait = async (verification: IdentityVerification) => {
    if (!verification.callUrl || !verification.form)
      throw new Error("KCP registration is incomplete");
    const popupName = "ondog-kcp-identity";
    const popup = window.open(
      "",
      popupName,
      "width=410,height=560,toolbar=no,status=no,menubar=no,scrollbars=yes,resizable=no",
    );
    if (!popup) throw new Error("KCP popup was blocked");
    const kcpForm = document.createElement("form");
    kcpForm.method = "post";
    kcpForm.action = verification.callUrl;
    kcpForm.target = popupName;
    for (const [name, value] of Object.entries(verification.form)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      kcpForm.appendChild(input);
    }
    document.body.appendChild(kcpForm);
    kcpForm.submit();
    kcpForm.remove();
    const deadline = Date.now() + 10 * 60_000;
    while (Date.now() < deadline) {
      await new Promise((resolve) => window.setTimeout(resolve, 1_000));
      if (!verification.pollToken)
        throw new Error("KCP polling token is missing");
      const current = await getPhoneVerification(
        verification.id,
        verification.pollToken,
      );
      if (current.status === "VERIFIED") {
        popup.close();
        return current;
      }
      if (popup.closed) throw new Error("KCP popup was closed");
    }
    popup.close();
    throw new Error("KCP verification timed out");
  };

  const verify = async () => {
    setError("");
    setVerifying(true);
    try {
      const started = await requestPhoneVerification({
        name: form.name,
        birthDate: form.birthDate,
        phone: form.phone.replace(/\D/g, ""),
      });
      const result =
        started.status === "VERIFIED" ? started : await openKcpAndWait(started);
      setForm((current) => ({ ...current, identityVerificationId: result.id }));
      setVerified(true);
      showToast(
        result.developmentMock
          ? "개발 환경 모의 KCP 본인인증이 완료되었습니다."
          : "NHN KCP 본인인증이 완료되었습니다.",
      );
    } catch {
      setError(
        "본인인증을 완료하지 못했습니다. 팝업 허용 여부와 입력 정보를 확인해주세요.",
      );
    } finally {
      setVerifying(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setPending(true);
    try {
      const response =
        mode === "signup"
          ? await signup({
              ...form,
              termsVersion: SIGNUP_TERMS_VERSION,
              privacyVersion: SIGNUP_TERMS_VERSION,
              requiredTermsAgreed: true,
              marketingConsent: agreements.marketing,
            })
          : await login(form.userId, form.password);
      showToast(
        mode === "signup" ? "회원가입이 완료되었습니다." : "로그인했습니다.",
      );
      if (mode === "login") {
        router.replace("/");
        return;
      }
      const requested = new URLSearchParams(window.location.search).get("next");
      const safeNext =
        requested?.startsWith("/") && !requested.startsWith("//")
          ? requested
          : null;
      router.push(
        safeNext ??
          (response.user.roles.includes("BREEDER")
            ? "/breeder/profile"
            : "/applications"),
      );
    } catch (error) {
      const apiMessage = axios.isAxiosError<{ message?: string }>(error)
        ? error.response?.data?.message
        : undefined;
      setError(
        mode === "signup"
          ? apiMessage?.trim() || "회원가입 요청을 처리하지 못했습니다."
          : "아이디 또는 비밀번호를 확인해주세요.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <main>
      <SiteHeader compact />
      <section className="auth-shell shell">
        <div className="auth-card">
          <span className="eyebrow">ONDOG ACCOUNT</span>
          <h1>{mode === "login" ? "로그인" : "회원가입"}</h1>
          <p>
            {mode === "login"
              ? "계정에 안전하게 로그인하세요."
              : "모든 회원은 하나의 계정으로 가입합니다. 브리더 활동은 가입 후 별도 신청과 운영 심사를 거쳐 승인됩니다."}
          </p>
          <div className="auth-tabs" aria-label="계정 메뉴">
            {mode === "login" ? (
              <><strong>로그인</strong><Link href="/signup">회원가입</Link></>
            ) : (
              <><Link href="/auth">로그인</Link><strong>회원가입</strong></>
            )}
          </div>
          <form onSubmit={submit}>
            {mode === "signup" && (
              <>
                <section
                  className="signup-agreements"
                  aria-labelledby="signup-agreements-title"
                >
                  <div className="signup-agreements-head">
                    <div>
                      <h2 id="signup-agreements-title">약관 동의</h2>
                      <small>검토용 초안 · 버전 {SIGNUP_TERMS_VERSION}</small>
                    </div>
                    <label>
                      <input
                        type="checkbox"
                        checked={allAgreed}
                        onChange={(event) =>
                          setAgreements({
                            service: event.target.checked,
                            privacy: event.target.checked,
                            identity: event.target.checked,
                            marketing: event.target.checked,
                          })
                        }
                      />
                      전체 동의
                    </label>
                  </div>
                  <p className="signup-legal-note">
                    필수 약관과 선택 동의를 구분했습니다. 정식 운영 전 법률
                    검토와 사업자 정보를 반영해 확정해야 합니다.
                  </p>
                  <div className="signup-agreement-list">
                    {signupAgreements.map((agreement) => (
                      <article key={agreement.key}>
                        <label>
                          <input
                            type="checkbox"
                            aria-label={`${agreement.required ? "필수" : "선택"} ${agreement.title}`}
                            checked={agreements[agreement.key]}
                            onChange={(event) =>
                              setAgreements((current) => ({
                                ...current,
                                [agreement.key]: event.target.checked,
                              }))
                            }
                          />
                          <span>
                            <b>
                              {agreement.required ? "[필수]" : "[선택]"}{" "}
                              {agreement.title}
                            </b>
                            <small>{agreement.summary}</small>
                          </span>
                        </label>
                        <details>
                          <summary>내용 보기</summary>
                          <div className="signup-terms-body">
                            {agreement.sections.map((section) => (
                              <section key={section.heading}>
                                <h3>{section.heading}</h3>
                                <p>{section.body}</p>
                              </section>
                            ))}
                          </div>
                        </details>
                      </article>
                    ))}
                  </div>
                </section>
                <div className="auth-field-grid">
                  <label>
                    성명
                    <input
                      required
                      readOnly={verified}
                      maxLength={32}
                      autoComplete="name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                    />
                  </label>
                  <label>
                    생년월일
                    <input
                      required
                      readOnly={verified}
                      type="date"
                      max={new Date().toISOString().slice(0, 10)}
                      autoComplete="bday"
                      value={form.birthDate}
                      onChange={(e) => update("birthDate", e.target.value)}
                    />
                  </label>
                </div>
                <label>
                  휴대폰 번호
                  <span className="auth-inline">
                    <input
                      required
                      readOnly={verified}
                      type="tel"
                      inputMode="numeric"
                      pattern="01[016789][0-9]{7,8}"
                      placeholder="01012345678"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={(e) =>
                        update("phone", e.target.value.replace(/\D/g, ""))
                      }
                    />
                    <button
                      type="button"
                      disabled={
                        !requiredAgreed ||
                        verifying ||
                        verified ||
                        !form.name ||
                        !form.birthDate ||
                        !/^01[016789]\d{7,8}$/.test(form.phone)
                      }
                      onClick={() => void verify()}
                    >
                      {verifying
                        ? "인증 진행 중"
                        : verified
                          ? "인증 완료"
                          : "NHN KCP 본인인증"}
                    </button>
                  </span>
                  <small className="auth-guidance">
                    KCP에서 확인한 성명·생년월일·휴대폰 번호로 가입합니다.
                  </small>
                </label>
                <label>
                  이메일 주소
                  <input
                    required
                    type="email"
                    maxLength={254}
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                  />
                </label>
              </>
            )}
            <label>
              아이디
              <input
                required
                minLength={4}
                maxLength={60}
                pattern="[a-zA-Z0-9_-]+"
                autoComplete="username"
                value={form.userId}
                onChange={(e) => update("userId", e.target.value)}
              />
            </label>
            <label>
              비밀번호
              <input
                required
                minLength={8}
                maxLength={72}
                type="password"
                autoComplete={
                  mode === "signup" ? "new-password" : "current-password"
                }
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
              />
            </label>
            {error && (
              <p className="auth-error" role="alert">
                {error}
              </p>
            )}
            <button
              className="auth-submit"
              disabled={
                pending || (mode === "signup" && (!verified || !requiredAgreed))
              }
            >
              {pending ? "처리 중" : mode === "login" ? "로그인" : "가입 시작"}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default function AuthPage() {
  return <AuthScreen initialMode="login" />;
}
